import { type ReactNode } from 'react';
import * as stylex from '@stylexjs/stylex';
import { sentinel } from '../theme/semantic.stylex';

/**
 * Branded loading indicator: a thin static track ring with a rotating
 * conic-gradient arc in the sentinel magenta and a gently pulsing core. Honours `prefers-reduced-motion` (the arc and core
 * settle into a steady three-quarter ring rather than spinning).
 *
 * Accessible by default: the root carries `role="status"` + an `aria-label`,
 * so screen readers announce the busy state. Pass `label` to render a visible
 * caption beneath the dial (also used as the accessible name when present).
 */

const spin = stylex.keyframes({
  to: { transform: 'rotate(360deg)' },
});

const corePulse = stylex.keyframes({
  '0%, 100%': { opacity: 0.3, transform: 'scale(0.7)' },
  '50%': { opacity: 1, transform: 'scale(1)' },
});

const captionReveal = stylex.keyframes({
  from: { opacity: 0, transform: 'translateY(4px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
});

const styles = stylex.create({
  wrap: {
    display: 'grid',
    justifyItems: 'center',
    gap: '0.875rem',
  },
  dial: {
    position: 'relative',
    display: 'grid',
    placeItems: 'center',
  },
  dialSm: { width: '24px', height: '24px' },
  dialMd: { width: '44px', height: '44px' },
  dialLg: { width: '60px', height: '60px' },
  // Full static track ring — the lane the arc travels in.
  track: {
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: sentinel.borderEmphasis,
  },
  // Rotating arc, masked from a conic gradient into a thin ring.
  arc: {
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    backgroundImage: `conic-gradient(from 90deg, transparent 0deg, color-mix(in oklch, ${sentinel.brandPrimary} 60%, transparent) 120deg, ${sentinel.brandPrimaryEmphasis} 300deg, transparent 330deg)`,
    WebkitMaskImage:
      'radial-gradient(farthest-side, transparent calc(100% - 2.5px), #000 calc(100% - 2px))',
    maskImage:
      'radial-gradient(farthest-side, transparent calc(100% - 2.5px), #000 calc(100% - 2px))',
    animationName: { default: spin, '@media (prefers-reduced-motion: reduce)': 'none' },
    animationDuration: '900ms',
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
  },
  // Pulsing core dot.
  core: {
    position: 'relative',
    borderRadius: '50%',
    backgroundColor: sentinel.brandPrimaryEmphasis,
    boxShadow: `0 0 10px color-mix(in oklch, ${sentinel.brandPrimary} 70%, transparent)`,
    animationName: { default: corePulse, '@media (prefers-reduced-motion: reduce)': 'none' },
    animationDuration: '1400ms',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
    opacity: { default: null, '@media (prefers-reduced-motion: reduce)': 0.9 },
  },
  coreSm: { width: '5px', height: '5px' },
  coreMd: { width: '8px', height: '8px' },
  coreLg: { width: '11px', height: '11px' },
  caption: {
    margin: 0,
    fontFamily: sentinel.fontBody,
    fontSize: '0.875rem',
    letterSpacing: '0.01em',
    color: sentinel.textMuted,
    textAlign: 'center',
    animationName: { default: captionReveal, '@media (prefers-reduced-motion: reduce)': 'none' },
    animationDuration: '420ms',
    animationTimingFunction: sentinel.easeOut,
    animationFillMode: 'both',
    animationDelay: '120ms',
  },
});

export interface SpinnerProps {
  /** Diameter preset. Default `md` (44px). */
  size?: 'sm' | 'md' | 'lg';
  /** Visible caption rendered beneath the dial; also the accessible name. */
  label?: ReactNode;
  /**
   * Accessible name when no visible `label` is shown. Default `'Loading'`.
   * Ignored when `label` is a non-empty string (the caption names it instead).
   */
  ariaLabel?: string;
}

const dialSize = { sm: styles.dialSm, md: styles.dialMd, lg: styles.dialLg } as const;
const coreSize = { sm: styles.coreSm, md: styles.coreMd, lg: styles.coreLg } as const;

export function Spinner({ size = 'md', label, ariaLabel = 'Loading' }: SpinnerProps) {
  const accessibleName = typeof label === 'string' && label.length > 0 ? label : ariaLabel;
  return (
    <div {...stylex.props(styles.wrap)} role="status" aria-label={accessibleName}>
      <span {...stylex.props(styles.dial, dialSize[size])}>
        <span {...stylex.props(styles.track)} aria-hidden="true" />
        <span {...stylex.props(styles.arc)} aria-hidden="true" />
        <span {...stylex.props(styles.core, coreSize[size])} aria-hidden="true" />
      </span>
      {label ? <p {...stylex.props(styles.caption)}>{label}</p> : null}
    </div>
  );
}
